"use client";

import React, { useState } from "react";
import { Target } from "lucide-react";
import { HorizontalScroll } from "@/components/ui/HorizontalScroll";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { GameCard } from "./GameCard";
import { ChallengeZoneModal } from "./ChallengeZoneModal";
import { useChallengeZoneGames } from "./useChallengeZoneGames";
import type { ChallengeZoneProps } from "./types";
import type { ChallengeZoneGame } from "./types";
import { Surface } from "@/components/ui/Surface";

/**
 * Challenge Zone — Carousel variant for main content areas.
 * Renders a horizontal scrollable carousel of game cards.
 * Returns null if no team entities or no games found.
 */
export function ChallengeZone({ relatedEntities, locale }: ChallengeZoneProps) {
    const labels = useLabels();
    const { gamification } = useTenantSettings();
    const { games, isLoading } = useChallengeZoneGames(relatedEntities, locale);
    const [selectedGame, setSelectedGame] = useState<ChallengeZoneGame | null>(null);

    if (!gamification.enabled) return null;

    // Don't render anything if no games (after loading completes)
    if (!isLoading && games.length === 0) return null;

    const czLabels = labels.challengeZone;

    return (
        <>
            <Surface slot="challengeZone" className="relative overflow-hidden">
                {/* Animated gradient top bar */}
                <div className="h-0.5 bg-primary" />

                <div className="p-5 md:p-6">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-linear-to-br from-primary to-accent w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0">
                            <Target className="w-4 h-4 text-primary-foreground" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-[15px]">{czLabels.title}</h3>
                            <p className="text-[11px] opacity-70 mt-0.5">
                                {czLabels.gamesAvailable.replace("{count}", String(games.length))}
                            </p>
                        </div>
                    </div>

                    {isLoading ? (
                        <ChallengeZoneSkeleton />
                    ) : (
                        <HorizontalScroll gap="gap-3" arrowSize="sm">
                            {games.map((game) => (
                                <div key={game.id} className="shrink-0 w-56 md:w-64">
                                    <GameCard game={game} labels={czLabels} onClick={() => setSelectedGame(game)} />
                                </div>
                            ))}
                        </HorizontalScroll>
                    )}
                </div>
            </Surface>

            <ChallengeZoneModal game={selectedGame} onClose={() => setSelectedGame(null)} />
        </>
    );
}

function ChallengeZoneSkeleton() {
    return (
        <div className="flex gap-3 overflow-hidden">
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className="shrink-0 w-56 md:w-64 h-[180px] rounded-xl bg-foreground/[0.03] animate-pulse"
                    style={{ animationDelay: `${i * 200}ms` }}
                />
            ))}
        </div>
    );
}
